/* ==========================================================================
   AL SULTAN — Product page, Phase 4
   Works alongside product-premium-motion.js; never touches Shopify form,
   variant or cart logic — it only listens to their events.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var FINE = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var DESKTOP_MQ = window.matchMedia('(min-width: 990px)');
  var WA_NUMBER = '96176969882';

  var CHECK_SVG =
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var CARET_SVG =
    '<svg viewBox="0 0 10 6" aria-hidden="true" focusable="false"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg>';
  var CLOSE_SVG =
    '<svg viewBox="0 0 18 18" aria-hidden="true" focusable="false"><path d="M2 2l14 14M16 2L2 16" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
  var WA_SVG =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 3.2a8.7 8.7 0 0 0-7.5 13.1L3.3 20.8l4.6-1.2A8.7 8.7 0 1 0 12 3.2Z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>';

  function clamp(v, min, max) {
    return v < min ? min : v > max ? max : v;
  }

  function onScrollFrame(fn) {
    var queued = false;
    function run() {
      queued = false;
      fn();
    }
    function request() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(run);
    }
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request);
    request();
    return request;
  }

  function largestSrc(img) {
    if (!img) return '';
    var set = img.getAttribute('srcset');
    if (!set) return img.currentSrc || img.src;
    var best = '';
    var bestW = 0;
    set.split(',').forEach(function (part) {
      var bits = part.trim().split(/\s+/);
      var w = parseInt(bits[1], 10) || 0;
      if (w > bestW) {
        bestW = w;
        best = bits[0];
      }
    });
    return best || img.currentSrc || img.src;
  }

  /* ── 1. Stage: light, recede, press and pointer tilt ─────────────────── */

  function initStage(root, stage) {
    var aura = document.createElement('div');
    aura.className = 'als-stage-aura';
    aura.setAttribute('aria-hidden', 'true');
    var floor = document.createElement('div');
    floor.className = 'als-stage-floor';
    floor.setAttribute('aria-hidden', 'true');
    stage.prepend(floor);
    stage.prepend(aura);

    if (REDUCED) return;

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        stage.classList.toggle('is-offscreen', !entries[0].isIntersecting);
      }).observe(stage);
    }

    var purchase = root.querySelector('.product-premium__purchase');
    var title = root.querySelector('.product-premium__purchase .product__title');

    onScrollFrame(function () {
      var rect = stage.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < window.innerHeight) {
        var recede = clamp(-rect.top / (rect.height * 0.85), 0, 1);
        stage.style.setProperty('--als-recede', recede.toFixed(3));
      }
      if (title && purchase && !DESKTOP_MQ.matches) {
        var header = document.querySelector('.section-header, sticky-header');
        var headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
        var t = title.getBoundingClientRect();
        var shift = clamp((headerBottom + 24 - t.top) / Math.max(t.height, 1), 0, 1);
        purchase.style.setProperty('--als-title-shift', shift.toFixed(3));
      } else if (purchase) {
        purchase.style.removeProperty('--als-title-shift');
      }
    });

    stage.addEventListener(
      'pointerdown',
      function (e) {
        if (e.pointerType === 'mouse') return;
        var item = e.target.closest('.product__media-item');
        if (!item) return;
        item.classList.add('is-pressed');
        var release = function () {
          item.classList.remove('is-pressed');
          window.removeEventListener('pointerup', release);
          window.removeEventListener('pointercancel', release);
        };
        window.addEventListener('pointerup', release);
        window.addEventListener('pointercancel', release);
      },
      { passive: true }
    );

    if (FINE) {
      var raf = 0;
      var px = 0;
      var py = 0;
      var apply = function () {
        raf = 0;
        var r = stage.getBoundingClientRect();
        var rx = clamp((px - r.left) / r.width, 0, 1);
        var ry = clamp((py - r.top) / r.height, 0, 1);
        stage.style.setProperty('--als-tilt-y', ((rx - 0.5) * 7).toFixed(2) + 'deg');
        stage.style.setProperty('--als-tilt-x', ((0.5 - ry) * 5).toFixed(2) + 'deg');
        stage.style.setProperty('--als-mx', (rx * 100).toFixed(1) + '%');
        stage.style.setProperty('--als-my', (ry * 100).toFixed(1) + '%');
      };
      stage.addEventListener('pointermove', function (e) {
        px = e.clientX;
        py = e.clientY;
        if (!raf) raf = requestAnimationFrame(apply);
      });
      stage.addEventListener('pointerleave', function () {
        stage.style.setProperty('--als-tilt-y', '0deg');
        stage.style.setProperty('--als-tilt-x', '0deg');
      });
    }
  }

  /* ── 2. Multi-image gallery: depth track + thumbnail rail ────────────── */

  function initGallery(root, stage) {
    var list = stage.querySelector('.product__media-list');
    if (!list) return null;
    var slides = Array.prototype.slice.call(list.querySelectorAll(':scope > .product__media-item'));
    if (slides.length < 2) return { slides: slides, list: list, goTo: function () {} };

    stage.classList.add('als-gallery-multi');
    list.classList.add('als-track');

    var rail = document.createElement('div');
    rail.className = 'als-rail';
    var thumbs = document.createElement('div');
    thumbs.className = 'als-rail__thumbs';
    thumbs.setAttribute('role', 'group');
    thumbs.setAttribute('aria-label', 'Product images');

    var prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'als-rail__arrow als-rail__arrow--prev';
    prev.setAttribute('aria-label', 'Previous image');
    prev.innerHTML = CARET_SVG;
    var next = prev.cloneNode(true);
    next.className = 'als-rail__arrow als-rail__arrow--next';
    next.setAttribute('aria-label', 'Next image');

    var current = 0;

    function goTo(i) {
      i = clamp(i, 0, slides.length - 1);
      list.scrollTo({ left: slides[i].offsetLeft - list.offsetLeft, behavior: REDUCED ? 'auto' : 'smooth' });
    }

    var thumbButtons = slides.map(function (slide, i) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'als-rail__thumb';
      b.setAttribute('aria-label', 'Show image ' + (i + 1) + ' of ' + slides.length);
      var src = slide.querySelector('img');
      if (src) {
        var im = document.createElement('img');
        im.src = src.currentSrc || src.src;
        im.alt = '';
        im.loading = 'lazy';
        b.appendChild(im);
      }
      b.addEventListener('click', function () {
        goTo(i);
      });
      thumbs.appendChild(b);
      return b;
    });

    prev.addEventListener('click', function () {
      goTo(current - 1);
    });
    next.addEventListener('click', function () {
      goTo(current + 1);
    });

    rail.appendChild(prev);
    rail.appendChild(thumbs);
    rail.appendChild(next);
    var gallery = stage.querySelector('media-gallery') || stage;
    gallery.appendChild(rail);

    function update() {
      var lr = list.getBoundingClientRect();
      var center = lr.left + lr.width / 2;
      var best = 0;
      var bestD = Infinity;
      slides.forEach(function (slide, i) {
        var r = slide.getBoundingClientRect();
        var d = clamp((r.left + r.width / 2 - center) / lr.width, -1, 1);
        var a = Math.abs(d);
        if (a < bestD) {
          bestD = a;
          best = i;
        }
        if (!REDUCED) {
          slide.style.setProperty('--als-slide-ry', (d * -32).toFixed(2) + 'deg');
          slide.style.setProperty('--als-slide-s', (1 - a * 0.14).toFixed(3));
          slide.style.setProperty('--als-slide-o', (1 - a * 0.55).toFixed(3));
          slide.style.setProperty('--als-slide-x', (d * -18).toFixed(1) + '%');
        }
      });
      if (best !== current || !thumbButtons[current].hasAttribute('aria-current')) {
        current = best;
        thumbButtons.forEach(function (b, i) {
          if (i === current) b.setAttribute('aria-current', 'true');
          else b.removeAttribute('aria-current');
        });
        slides.forEach(function (s, i) {
          s.classList.toggle('is-active', i === current);
        });
        prev.disabled = current === 0;
        next.disabled = current === slides.length - 1;
      }
    }

    var queued = false;
    list.addEventListener(
      'scroll',
      function () {
        if (queued) return;
        queued = true;
        requestAnimationFrame(function () {
          queued = false;
          update();
        });
      },
      { passive: true }
    );
    window.addEventListener('resize', update);
    update();

    return { slides: slides, list: list, goTo: goTo };
  }

  /* ── 3. Fullscreen viewer with pinch, double-tap and wheel zoom ──────── */

  function createViewer(title) {
    var el = document.createElement('div');
    el.className = 'als-viewer';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', (title || 'Product') + ' images');
    el.innerHTML =
      '<div class="als-viewer__bar"><span class="als-viewer__count" aria-live="polite"></span>' +
      '<button type="button" class="als-viewer__close" aria-label="Close">' +
      CLOSE_SVG +
      '</button></div><div class="als-viewer__track"></div>' +
      '<div class="als-viewer__hint"><div class="als-viewer__dots" aria-hidden="true"></div>' +
      '<span>Pinch or double-tap to zoom</span></div>';
    document.body.appendChild(el);
    return el;
  }

  function initViewer(root, stage, gallery) {
    var slides = (gallery && gallery.slides) || [];
    var items = slides.filter(function (s) {
      return s.querySelector('.media-type-image img');
    });
    if (!items.length) return;

    var titleEl = root.querySelector('.product__title h1');
    var view = null;
    var track;
    var countEl;
    var dotsEl;
    var frames = [];
    var index = 0;
    var opener = null;
    var z = { s: 1, x: 0, y: 0 };
    var pointers = new Map();
    var gesture = null;
    var lastTap = 0;

    function build() {
      view = createViewer(titleEl ? titleEl.textContent.trim() : '');
      track = view.querySelector('.als-viewer__track');
      countEl = view.querySelector('.als-viewer__count');
      dotsEl = view.querySelector('.als-viewer__dots');
      items.forEach(function (slide) {
        var src = slide.querySelector('img');
        var s = document.createElement('div');
        s.className = 'als-viewer__slide';
        var img = document.createElement('img');
        img.className = 'als-viewer__img';
        img.alt = src.alt || '';
        img.draggable = false;
        img.dataset.src = largestSrc(src);
        img.src = src.currentSrc || src.src;
        s.appendChild(img);
        track.appendChild(s);
        frames.push(img);
        if (items.length > 1) {
          var d = document.createElement('span');
          d.className = 'als-viewer__dot';
          dotsEl.appendChild(d);
        }
      });
      view.querySelector('.als-viewer__close').addEventListener('click', close);
      view.addEventListener('keydown', onKey);
      track.addEventListener('pointerdown', onDown);
      track.addEventListener('pointermove', onMove);
      track.addEventListener('pointerup', onUp);
      track.addEventListener('pointercancel', onUp);
      track.addEventListener('wheel', onWheel, { passive: false });
    }

    function img() {
      return frames[index];
    }

    function applyZoom(animate) {
      var im = img();
      im.style.transition = animate ? 'transform 0.32s cubic-bezier(0.22,1,0.36,1)' : 'none';
      im.style.transform = 'translate3d(' + z.x + 'px,' + z.y + 'px,0) scale(' + z.s + ')';
    }

    function base() {
      var im = img();
      var slide = im.parentNode.getBoundingClientRect();
      return {
        left: im.offsetLeft,
        top: im.offsetTop,
        w: im.offsetWidth,
        h: im.offsetHeight,
        cw: slide.width,
        ch: slide.height,
        cl: slide.left,
        ct: slide.top,
      };
    }

    function bound() {
      var b = base();
      var w = b.w * z.s;
      var h = b.h * z.s;
      z.x = w <= b.cw ? (b.cw - w) / 2 - b.left : clamp(z.x, b.cw - b.left - w, -b.left);
      z.y = h <= b.ch ? (b.ch - h) / 2 - b.top : clamp(z.y, b.ch - b.top - h, -b.top);
      if (z.s === 1) {
        z.x = 0;
        z.y = 0;
      }
    }

    function zoomAt(clientX, clientY, scale, animate) {
      var b = base();
      var mx = clientX - b.cl - b.left;
      var my = clientY - b.ct - b.top;
      var cx = (mx - z.x) / z.s;
      var cy = (my - z.y) / z.s;
      z.s = clamp(scale, 1, 4);
      z.x = mx - cx * z.s;
      z.y = my - cy * z.s;
      bound();
      applyZoom(animate);
    }

    function resetZoom(animate) {
      z = { s: 1, x: 0, y: 0 };
      if (frames.length) applyZoom(animate);
    }

    function setIndex(i, animate) {
      index = clamp(i, 0, frames.length - 1);
      frames.forEach(function (f, n) {
        var slide = f.parentNode;
        slide.style.transition = animate && !REDUCED ? '' : 'none';
        slide.style.transform = 'translate3d(' + -index * 100 + '%,0,0)';
        if (n !== index) {
          f.style.transform = '';
        }
      });
      z = { s: 1, x: 0, y: 0 };
      var hi = img();
      if (hi.dataset.src && hi.src !== hi.dataset.src) hi.src = hi.dataset.src;
      countEl.textContent = frames.length > 1 ? index + 1 + ' / ' + frames.length : '';
      Array.prototype.forEach.call(dotsEl.children, function (d, n) {
        d.classList.toggle('is-active', n === index);
      });
    }

    function open(i, from) {
      if (!view) build();
      opener = from || document.activeElement;
      setIndex(i, false);
      document.documentElement.classList.add('als-viewer-lock');
      view.classList.add('is-open');
      view.querySelector('.als-viewer__close').focus({ preventScroll: true });

      var sourceImg = items[i] && items[i].querySelector('img');
      var im = img();
      if (!REDUCED && sourceImg && im.animate) {
        requestAnimationFrame(function () {
          var a = sourceImg.getBoundingClientRect();
          var b = im.getBoundingClientRect();
          if (!b.width || !a.width) return;
          var sx = a.width / b.width;
          im.parentNode.animate(
            [
              {
                transform:
                  'translate3d(' +
                  (a.left + a.width / 2 - (b.left + b.width / 2)) +
                  'px,' +
                  (a.top + a.height / 2 - (b.top + b.height / 2)) +
                  'px,0) scale(' +
                  sx +
                  ')',
              },
              { transform: 'translate3d(' + -index * 100 + '%,0,0)' },
            ],
            { duration: 460, easing: 'cubic-bezier(0.22,1,0.36,1)' }
          );
        });
      }
    }

    function close() {
      if (!view) return;
      view.classList.remove('is-open');
      document.documentElement.classList.remove('als-viewer-lock');
      resetZoom(false);
      if (gallery && gallery.goTo && items.length > 1) gallery.goTo(slides.indexOf(items[index]));
      if (opener && opener.focus) opener.focus({ preventScroll: true });
    }

    function onKey(e) {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') setIndex(index + 1, true);
      else if (e.key === 'ArrowLeft') setIndex(index - 1, true);
      else if (e.key === 'Tab') {
        e.preventDefault();
        view.querySelector('.als-viewer__close').focus();
      }
    }

    function onWheel(e) {
      e.preventDefault();
      zoomAt(e.clientX, e.clientY, z.s * (e.deltaY < 0 ? 1.15 : 1 / 1.15), false);
    }

    function dist(a, b) {
      return Math.hypot(a.x - b.x, a.y - b.y);
    }

    function onDown(e) {
      try {
        track.setPointerCapture(e.pointerId);
      } catch (err) {
        /* pointer already released */
      }
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 2) {
        var p = Array.from(pointers.values());
        gesture = { type: 'pinch', d: dist(p[0], p[1]), s: z.s };
      } else if (pointers.size === 1) {
        gesture = { type: 'pan', sx: e.clientX, sy: e.clientY, x: z.x, y: z.y, moved: false, t: Date.now() };
      }
    }

    function onMove(e) {
      if (!pointers.has(e.pointerId) || !gesture) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (gesture.type === 'pinch' && pointers.size === 2) {
        var p = Array.from(pointers.values());
        zoomAt((p[0].x + p[1].x) / 2, (p[0].y + p[1].y) / 2, gesture.s * (dist(p[0], p[1]) / gesture.d), false);
        return;
      }
      if (gesture.type !== 'pan') return;
      var dx = e.clientX - gesture.sx;
      var dy = e.clientY - gesture.sy;
      if (Math.abs(dx) + Math.abs(dy) > 6) gesture.moved = true;
      if (z.s > 1) {
        z.x = gesture.x + dx;
        z.y = gesture.y + dy;
        bound();
        applyZoom(false);
      } else if (Math.abs(dx) > Math.abs(dy) && frames.length > 1) {
        gesture.axis = 'x';
        frames.forEach(function (f) {
          f.parentNode.style.transition = 'none';
          f.parentNode.style.transform = 'translate3d(calc(' + -index * 100 + '% + ' + dx + 'px),0,0)';
        });
      } else if (dy > 0) {
        gesture.axis = 'y';
        img().parentNode.style.transition = 'none';
        img().parentNode.style.transform =
          'translate3d(' + -index * 100 + '%,' + dy + 'px,0) scale(' + (1 - Math.min(dy / 1600, 0.12)) + ')';
        view.style.opacity = String(1 - Math.min(dy / 500, 0.6));
      }
    }

    function onUp(e) {
      if (!pointers.has(e.pointerId)) return;
      pointers.delete(e.pointerId);
      if (!gesture) return;
      if (gesture.type === 'pinch') {
        if (pointers.size === 0) gesture = null;
        if (z.s < 1.05) resetZoom(true);
        return;
      }
      var dx = e.clientX - gesture.sx;
      var dy = e.clientY - gesture.sy;
      view.style.opacity = '';
      if (!gesture.moved) {
        var now = Date.now();
        if (now - lastTap < 300) {
          if (z.s > 1) resetZoom(true);
          else zoomAt(e.clientX, e.clientY, 2.5, true);
          lastTap = 0;
        } else {
          lastTap = now;
        }
      } else if (gesture.axis === 'x') {
        setIndex(dx < -60 ? index + 1 : dx > 60 ? index - 1 : index, true);
      } else if (gesture.axis === 'y') {
        if (dy > 120) close();
        else setIndex(index, true);
      }
      gesture = null;
    }

    // Images open in this viewer instead of the stacked lightbox; video and 3D keep Dawn's modal.
    // Dawn's toggle button collapses to 0×0 in this layout, so listen for a
    // tap anywhere on an image slide (the bottle, the zoom icon or the toggle).
    root.addEventListener(
      'click',
      function (e) {
        var slide = e.target.closest('.product__media-item');
        if (!slide || !stage.contains(slide)) return;
        if (e.target.closest('.deferred-media, product-model, .product__xr-button')) return;
        var i = items.indexOf(slide);
        if (i < 0) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        open(i, slide.querySelector('.product__media-toggle') || slide);
      },
      true
    );
  }

  /* ── 4. Editorial title, availability and sticky price ───────────────── */

  function splitTitle(root, purchase) {
    var h1 = purchase.querySelector('.product__title h1');
    if (!h1 || h1.dataset.alsSplit) return;
    h1.dataset.alsSplit = 'true';
    var words = h1.textContent.trim().split(/\s+/);
    h1.setAttribute('aria-label', h1.textContent.trim());
    h1.textContent = '';
    words.forEach(function (w, i) {
      var outer = document.createElement('span');
      outer.className = 'als-word';
      outer.setAttribute('aria-hidden', 'true');
      var inner = document.createElement('span');
      inner.style.setProperty('--i', i);
      inner.textContent = w;
      outer.appendChild(inner);
      h1.appendChild(outer);
      if (i < words.length - 1) h1.appendChild(document.createTextNode(' '));
    });
    h1.parentNode.classList.add('als-split');
    if (!REDUCED) purchase.classList.add('als-title-ready');
  }

  function readPrice(root, sectionId) {
    var price = root.querySelector('#price-' + sectionId);
    if (!price) return '';
    var onSale = price.querySelector('.price--on-sale');
    var el = onSale
      ? price.querySelector('.price__sale .price-item--sale')
      : price.querySelector('.price__regular .price-item--regular');
    return el ? el.textContent.trim() : '';
  }

  function initStatus(root, sectionId) {
    var avail = root.querySelector('[data-als-avail]');
    var availText = root.querySelector('[data-als-avail-text]');
    var stickyPrice = root.querySelector('[data-als-sticky-price]');

    function sync(variant) {
      if (avail && availText) {
        var available = !!(variant && variant.available);
        avail.classList.toggle('is-sold-out', !available);
        availText.textContent = !variant
          ? (window.variantStrings && window.variantStrings.unavailable) || 'Unavailable'
          : available
          ? 'Available'
          : (window.variantStrings && window.variantStrings.soldOut) || 'Sold out';
      }
      if (stickyPrice) {
        var p = readPrice(root, sectionId);
        if (p) stickyPrice.textContent = p;
      }
    }

    // Unavailable combinations never publish variantChange, so also mirror the
    // button's own state. Disabled with the normal label means "loading": ignore.
    var atc = root.querySelector('#ProductSubmitButton-' + sectionId);
    if (atc && avail && availText && 'MutationObserver' in window) {
      new MutationObserver(function () {
        var label = (atc.querySelector('span') || atc).textContent.trim();
        var strings = window.variantStrings || {};
        if (!atc.disabled) {
          avail.classList.remove('is-sold-out');
          availText.textContent = 'Available';
        } else if (label === strings.soldOut || label === strings.unavailable) {
          avail.classList.add('is-sold-out');
          availText.textContent = label;
        }
      }).observe(atc, { attributes: true, attributeFilter: ['disabled'], childList: true, subtree: true, characterData: true });
    }

    // Match the main price's money format from the start.
    if (stickyPrice) {
      var initial = readPrice(root, sectionId);
      if (initial) stickyPrice.textContent = initial;
    }

    // product-info.js swaps the price markup before publishing variantChange.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        if (!event || !event.data || event.data.sectionId !== sectionId) return;
        sync(event.data.variant);
      });
    }
  }

  /* ── 5. Fragrance experience — parsed only from the real description ── */

  var NOTE_LIBRARY = {
    bergamot: 'Sparkling Italian citrus peel, bright with a gently bitter edge.',
    lemon: 'Zesty, sunlit citrus that lifts and freshens.',
    'lemon peel': 'The aromatic, slightly bitter zest of lemon.',
    lime: 'Sharp green citrus with a cooling snap.',
    mandarin: 'Soft, sweet citrus — rounder and juicier than lemon.',
    'green mandarin': 'Tart, unripe mandarin with a green, sparkling edge.',
    tangerine: 'Sweet, juicy citrus with a sunny warmth.',
    orange: 'Juicy orange fruit, sweet and radiant.',
    'sweet orange': 'Ripe, sugared orange — warm and cheerful.',
    'blood orange': 'Deep, tangy citrus with a red-berry nuance.',
    grapefruit: 'Bittersweet, effervescent citrus.',
    neroli: 'Bitter-orange blossom: luminous, green and softly honeyed.',
    'orange blossom': 'Heady white blossom of the orange tree, honeyed and bright.',
    petitgrain: 'Leaves and twigs of the bitter orange — green and woody-citrus.',
    'pink pepper': 'A rosy, sparkling spice with a hint of berry.',
    'black pepper': 'Dry, crackling spice that adds bite.',
    cardamom: 'Cool, aromatic spice with a eucalyptus-like freshness.',
    saffron: 'Precious, leathery-honeyed spice prized in Middle Eastern perfumery.',
    cinnamon: 'Warm, sweet bark spice.',
    nutmeg: 'Warm, nutty spice with a soft woodiness.',
    clove: 'Intense, warm spice with a medicinal edge.',
    ginger: 'Zingy, warm-fresh root spice.',
    lavender: 'Herbal and clean, with a calming floral-aromatic character.',
    sage: 'Aromatic herb with a dry, slightly camphorous freshness.',
    'clary sage': 'Herbal, musky sage with a soft, tea-like warmth.',
    rosemary: 'Fresh, resinous Mediterranean herb.',
    mint: 'Cool, crisp and invigorating.',
    basil: 'Green, slightly anise-like herb.',
    'green notes': 'The crisp scent of crushed leaves and stems.',
    apple: 'Crisp, juicy orchard fruit.',
    'red apple': 'Ripe red apple — sweet, crisp and juicy.',
    pear: 'Juicy, delicate fruit with a watery sweetness.',
    pineapple: 'Tropical, tangy-sweet fruit.',
    peach: 'Velvety, ripe stone fruit.',
    plum: 'Dark, jammy fruit with depth.',
    raspberry: 'Tart-sweet red berry.',
    blackcurrant: 'Tangy dark berry with a green, leafy facet.',
    cassis: 'Blackcurrant — tangy, fruity and slightly green.',
    melon: 'Watery, fresh and gently sweet.',
    coconut: 'Creamy, milky and tropical.',
    cherry: 'Sweet, almond-tinged red fruit.',
    fig: 'Milky, green and softly sweet.',
    rose: 'The queen of flowers — velvety, romantic and rich.',
    'damask rose': 'Prized Taif-style rose: honeyed, spicy and deep.',
    'turkish rose': 'Lush, deep rose with a jammy richness.',
    jasmine: 'Opulent white flower, sensual and radiant.',
    'sambac jasmine': 'Jasmine sambac — fruity, green and intensely floral.',
    'jasmine sambac': 'Fruity, green and intensely floral jasmine.',
    tuberose: 'Creamy, heady white flower of great presence.',
    iris: 'Powdery, cool and elegant, with a soft woody depth.',
    orris: 'Iris root — buttery, powdery and refined.',
    violet: 'Powdery, sweet petals with a green leaf.',
    peony: 'Fresh, airy rose-like flower with a dewy lightness.',
    'chinese peony': 'Airy, fresh peony with a soft rosy glow.',
    'lily of the valley': 'Delicate, dewy spring bloom.',
    magnolia: 'Creamy, lemony white flower.',
    gardenia: 'Lush, creamy white flower with green facets.',
    ylang: 'Exotic, creamy flower with a banana-like sweetness.',
    'ylang-ylang': 'Exotic, creamy flower with a banana-like sweetness.',
    'white flowers': 'A soft accord of creamy white blossoms.',
    heliotrope: 'Powdery, almond-vanilla flower.',
    geranium: 'Rosy, green and slightly minty.',
    vanilla: 'Warm, creamy sweetness that comforts and lingers.',
    vanillin: 'The sweet, creamy heart of vanilla.',
    'tonka bean': 'Warm almond-vanilla sweetness with a hay-like facet.',
    tonka: 'Warm almond-vanilla sweetness with a hay-like facet.',
    caramel: 'Golden, buttery sugar.',
    praline: 'Toasted nuts in caramelised sugar.',
    honey: 'Rich, golden and nectar-sweet.',
    chocolate: 'Dark, cocoa-rich gourmand.',
    cocoa: 'Bitter-sweet, roasted cacao.',
    coffee: 'Roasted, dark and energising.',
    almond: 'Sweet, nutty and marzipan-like.',
    musk: 'Soft, skin-like warmth that gives a lasting, clean trail.',
    'white musk': 'Clean, cottony musk — soft and airy.',
    amber: 'Warm, resinous glow — sweet, powdery and enveloping.',
    ambergris: 'Salty, mineral warmth with a radiant glow.',
    ambroxan: 'Radiant, mineral-woody amber with a skin-like warmth.',
    amberwood: 'Dry, radiant woody amber.',
    cedar: 'Dry, pencil-shaving woods.',
    cedarwood: 'Dry, pencil-shaving woods.',
    sandalwood: 'Creamy, smooth and milky wood.',
    vetiver: 'Earthy, smoky grass root.',
    patchouli: 'Earthy, dark and slightly sweet leaf.',
    oud: 'Agarwood — dark, resinous and animalic; the soul of Arabian perfumery.',
    agarwood: 'Oud — dark, resinous and deeply precious.',
    oakmoss: 'Damp, forest-floor moss with an inky depth.',
    leather: 'Smooth, smoky hide.',
    tobacco: 'Sweet, dried tobacco leaf with a honeyed warmth.',
    incense: 'Smoky, resinous and spiritual.',
    frankincense: 'Bright, lemony smoke of sacred resin.',
    olibanum: 'Frankincense — bright, lemony resin smoke.',
    myrrh: 'Bittersweet, balsamic resin.',
    labdanum: 'Dark, leathery-amber resin.',
    benzoin: 'Warm, vanilla-like balsam.',
    'guaiac wood': 'Smoky, slightly sweet wood.',
    cashmeran: 'Soft, musky woods with a velvety warmth.',
    cashmere: 'Soft, cosy woody-musk impression.',
    'cashmere wood': 'Soft, cosy woody-musk impression.',
    'marine notes': 'Fresh sea breeze and salty air.',
    'sea notes': 'Fresh sea breeze and salty air.',
    'aquatic notes': 'Watery freshness evoking open sea.',
    'fruity notes': 'A juicy accord of mixed fruits.',
    'sweet notes': 'A soft, sugared accord.',
    'woody notes': 'A warm accord of dry woods.',
  };

  var TIERS = {
    top: { name: 'Top notes', short: 'top', role: 'Top notes form the first impression — bright and immediate, they fade first.' },
    heart: { name: 'Heart notes', short: 'heart', role: 'Heart notes emerge as the opening softens and shape the character of the scent.' },
    base: { name: 'Base notes', short: 'base', role: 'Base notes are the foundation — the deepest accords, lingering longest on skin.' },
  };

  var PROFILE_KEYS = [
    { re: /^(?:type|family|fragrance family|olfactive family|scent family)$/i, key: 'family', label: 'Family' },
    { re: /^character(?:istics)?$/i, key: 'character', label: 'Character', chips: true, wide: true },
    { re: /^gender$/i, key: 'gender', label: 'For' },
    { re: /^(?:best )?seasons?$/i, key: 'season', label: 'Seasons' },
    { re: /^(?:best for|occasions?|best occasions?)$/i, key: 'occasion', label: 'Best for', wide: true },
    { re: /^longevity$/i, key: 'longevity', label: 'Longevity' },
    { re: /^sillage$/i, key: 'sillage', label: 'Sillage' },
    { re: /^projection$/i, key: 'projection', label: 'Projection' },
    { re: /^intensity$/i, key: 'intensity', label: 'Intensity' },
    { re: /^concentration$/i, key: 'concentration', label: 'Concentration' },
  ];

  function tierKey(word) {
    word = word.toLowerCase();
    if (word === 'top' || word === 'head' || word === 'opening') return 'top';
    if (word === 'heart' || word === 'middle') return 'heart';
    return 'base';
  }

  // Words that only appear in prose, never in a note name.
  var PROSE_RE = /\b(this|that|which|was|were|is|are|it|its|you|your|creat\w*|gives?|adds?|while|opens?|blends?|scent|fragrance|perfume)\b/i;
  // Adjectives are fine inside a labelled list ("Sweet Orange") but signal
  // description rather than notes in free-running sentences.
  var ADJ_RE = /\b(bright|fresh|sweet|warm|juicy|light|soft|rich|deep|smooth|sensual|lasting|elegant|vibrant|beautiful|perfect|delicate|intense)\b/i;

  function splitNotes(text, strict) {
    // Keep "Oriental Balm (Tolu Balm, Labdanum)" together: protect separators inside brackets.
    var guarded = text.replace(/\([^)]*\)/g, function (group) {
      return group.replace(/,/g, '\u0001').replace(/\band\b/gi, '\u0002');
    });
    var notes = guarded
      .split(/\s*(?:,|;|\band\b|&|\/|\+)\s*/i)
      .map(function (n) {
        return n
          .replace(/\u0001/g, ',')
          .replace(/\u0002/g, 'and')
          .replace(/^(?:of|with|notes? of)\s+/i, '')
          .replace(/[.:!]+$/, '')
          .trim();
      })
      .filter(function (n) {
        return n;
      });
    // A tier is only trusted when every item reads like a note name.
    var clean = notes.every(function (n) {
      return (
        n.length <= 30 &&
        n.split(/\s+/).length <= 4 &&
        !/\d/.test(n) &&
        !PROSE_RE.test(n) &&
        (strict || !ADJ_RE.test(n))
      );
    });
    return clean ? notes.slice(0, 12) : [];
  }

  var LABEL_RE = /\b(top|head|heart|middle|base)\s+notes?\s*(?::|–|—|-|\bare\b|\bis\b|\binclude\b|\binclude[sd]?\b)\s*|\b(top|head|opening|heart|middle|base)\s*:\s*/gi;

  // Splits text at every tier label; each value runs to the next label,
  // line break or sentence end. In strict mode the text must open with a label.
  function extractTiers(text, strict) {
    var tiers = { top: [], heart: [], base: [] };
    var marks = [];
    var m;
    LABEL_RE.lastIndex = 0;
    while ((m = LABEL_RE.exec(text))) {
      marks.push({ key: tierKey(m[1] || m[2]), start: m.index, end: LABEL_RE.lastIndex, bare: !m[1] });
    }
    if (!marks.length) return tiers;
    if (strict && text.slice(0, marks[0].start).trim()) return tiers;
    // Bare "Base:" labels are trusted in a list item, or in prose that also
    // uses a full "... notes" label.
    var hasFull = marks.some(function (mk) {
      return !mk.bare;
    });
    var rejected = false;
    marks.forEach(function (mk, i) {
      if (mk.bare && !strict && !hasFull) return;
      var value = text.slice(mk.end, i + 1 < marks.length ? marks[i + 1].start : text.length);
      value = value.split(/\n|\.(?:\s|$)/)[0].trim();
      if (!value) return;
      var notes = splitNotes(value, strict);
      if (notes.length) tiers[mk.key] = tiers[mk.key].concat(notes);
      else rejected = true;
    });
    // All or nothing: a tier that reads like prose means the whole parse is
    // untrustworthy, so show the fallback rather than a partial pyramid.
    return rejected ? { top: [], heart: [], base: [] } : tiers;
  }

  // "<p><strong>Top Notes</strong></p><ul><li>Peach</li>…</ul>" layouts
  function extractHeadingLists(desc) {
    var tiers = { top: [], heart: [], base: [] };
    var used = [];
    var ok = true;
    desc.querySelectorAll('p, h3, h4, h5, h6').forEach(function (head) {
      var m = head.textContent.trim().match(/^(top|head|heart|middle|base)\s+notes?\s*:?$/i);
      var list = head.nextElementSibling;
      if (!m || !list || !/^(UL|OL)$/.test(list.tagName)) return;
      var notes = [];
      Array.prototype.forEach.call(list.children, function (li) {
        var n = splitNotes(li.textContent.replace(/\s+/g, ' ').trim(), true);
        if (n.length) notes = notes.concat(n);
        else ok = false;
      });
      tiers[tierKey(m[1])] = tiers[tierKey(m[1])].concat(notes);
      used.push(head, list);
    });
    if (!ok) return { tiers: { top: [], heart: [], base: [] }, used: [] };
    return { tiers: tiers, used: used };
  }

  function textWithBreaks(node) {
    var clone = node.cloneNode(true);
    clone.querySelectorAll('br').forEach(function (br) {
      br.replaceWith('\n');
    });
    return clone.textContent.replace(/[ \t ]+/g, ' ').trim();
  }

  function countTiers(t) {
    return t.top.length + t.heart.length + t.base.length;
  }

  function markMovedList(list) {
    if (!list) return;
    var live = Array.prototype.filter.call(list.children, function (li) {
      return !li.hasAttribute('data-als-moved');
    });
    if (live.length) return;
    list.setAttribute('data-als-moved', '');
    var prev = list.previousElementSibling;
    if (prev && /^(H[2-6]|P)$/.test(prev.tagName) && prev.textContent.trim().length < 40 && /notes|profile|pyramid/i.test(prev.textContent)) {
      prev.setAttribute('data-als-moved', '');
    }
  }

  function parseDescription(desc) {
    var tiers = { top: [], heart: [], base: [] };
    var profile = {};
    if (!desc) return { tiers: tiers, profile: profile, found: false };

    var profRe = /^\s*([A-Za-z ]{3,24}?)\s*:\s*(.+)$/;

    var headed = extractHeadingLists(desc);
    if (countTiers(headed.tiers)) {
      tiers = headed.tiers;
      headed.used.forEach(function (n) {
        n.setAttribute('data-als-moved', '');
      });
      var first = headed.used[0] && headed.used[0].previousElementSibling;
      if (first && /^fragrance notes:?$/i.test(first.textContent.trim())) first.setAttribute('data-als-moved', '');
    }

    desc.querySelectorAll('li, p').forEach(function (el) {
      if (el.querySelector('li, p') || el.hasAttribute('data-als-moved') || el.closest('[data-als-moved]')) return;
      var found = extractTiers(textWithBreaks(el), true);
      if (countTiers(found)) {
        ['top', 'heart', 'base'].forEach(function (k) {
          tiers[k] = tiers[k].concat(found[k]);
        });
        el.setAttribute('data-als-moved', '');
        if (el.tagName === 'LI') markMovedList(el.parentNode);
        return;
      }
      var text = el.textContent.replace(/\s+/g, ' ').trim();
      if (el.tagName !== 'LI') return;
      var p = text.match(profRe);
      if (!p) return;
      var label = p[1].trim();
      if (/^size$/i.test(label)) {
        el.setAttribute('data-als-moved', '');
        markMovedList(el.parentNode);
        return;
      }
      for (var i = 0; i < PROFILE_KEYS.length; i++) {
        if (PROFILE_KEYS[i].re.test(label)) {
          profile[PROFILE_KEYS[i].key] = p[2].trim();
          el.setAttribute('data-als-moved', '');
          markMovedList(el.parentNode);
          break;
        }
      }
    });

    var found = countTiers(tiers) > 0;

    // Sentence form: "Top note is X; middle note is Y; base notes are Z."
    // Requires the word "notes" and rejects any tier that reads like prose.
    if (!found) {
      tiers = extractTiers(textWithBreaks(desc), false);
      found = countTiers(tiers) > 0;
    }

    // A single tier is not a pyramid; leave such copy in the story.
    if (['top', 'heart', 'base'].filter(function (k) { return tiers[k].length; }).length < 2) {
      desc.querySelectorAll('[data-als-moved]').forEach(function (n) {
        n.removeAttribute('data-als-moved');
      });
      tiers = { top: [], heart: [], base: [] };
      found = false;
    }

    // If profile items were pulled but no notes were, keep the profile list visible in the story
    return { tiers: tiers, profile: profile, found: found };
  }

  function isFamilyLike(type) {
    return (
      !!type &&
      /(woody|floral|oriental|amber|aromatic|fresh|citrus|fruity|gourmand|spicy|leather|aquatic|chypre|foug|powdery|vanilla|oud|green)/i.test(type) &&
      !/(refill|set|body|lotion|package|bath)/i.test(type)
    );
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function buildPyramid(mount, data, family) {
    mount.innerHTML = '';
    mount.classList.remove('als-scent--fallback');

    var head = el('div', 'als-scent__head');
    head.appendChild(el('p', 'als-scent__eyebrow', 'Fragrance pyramid'));
    head.appendChild(el('h2', 'als-scent__title', 'The Composition'));
    if (family) head.appendChild(el('span', 'als-scent__family', family));
    mount.appendChild(head);

    var pyramid = el('ol', 'als-pyramid');
    pyramid.setAttribute('aria-label', 'Fragrance notes');
    var card = el('div', 'als-note-card');
    card.hidden = true;
    card.setAttribute('aria-live', 'polite');
    var activeBtn = null;
    var n = 0;

    ['top', 'heart', 'base'].forEach(function (key) {
      var notes = data.tiers[key];
      if (!notes.length) return;
      var tier = el('li', 'als-tier');
      tier.dataset.tier = key;
      tier.style.setProperty('--i', n);
      n++;
      var label = el('p', 'als-tier__label');
      label.appendChild(el('span', 'als-tier__num', ['I', 'II', 'III'][['top', 'heart', 'base'].indexOf(key)]));
      label.appendChild(document.createTextNode(TIERS[key].name));
      tier.appendChild(label);
      var ul = el('ul', 'als-notes');
      notes.forEach(function (note) {
        var li = el('li');
        var b = el('button', 'als-note', note);
        b.type = 'button';
        b.setAttribute('aria-expanded', 'false');
        b.addEventListener('click', function () {
          var same = activeBtn === b;
          if (activeBtn) activeBtn.setAttribute('aria-expanded', 'false');
          pyramid.querySelectorAll('.als-tier').forEach(function (t) {
            t.classList.remove('is-focus');
          });
          if (same) {
            activeBtn = null;
            card.hidden = true;
            pyramid.classList.remove('has-active');
            return;
          }
          activeBtn = b;
          b.setAttribute('aria-expanded', 'true');
          tier.classList.add('is-focus');
          pyramid.classList.add('has-active');
          var others = notes.filter(function (x) {
            return x !== note;
          });
          var desc = NOTE_LIBRARY[note.toLowerCase()];
          card.innerHTML = '';
          card.appendChild(el('p', 'als-note-card__name', note));
          if (desc) card.appendChild(el('p', 'als-note-card__desc', desc));
          var rel =
            note +
            ' is ' +
            (notes.length > 1 ? 'one of the ' + TIERS[key].short + ' notes' : 'the ' + TIERS[key].short + ' note') +
            ' of this fragrance' +
            (others.length ? ', alongside ' + others.slice(0, 3).join(', ') + '. ' : '. ') +
            TIERS[key].role;
          card.appendChild(el('p', 'als-note-card__rel', rel));
          tier.appendChild(card);
          card.hidden = false;
          card.style.animation = 'none';
          void card.offsetWidth;
          card.style.animation = '';
        });
        li.appendChild(b);
        ul.appendChild(li);
      });
      tier.appendChild(ul);
      pyramid.appendChild(tier);
    });

    if (n) {
      mount.appendChild(pyramid);
      mount.appendChild(el('p', 'als-scent__hint', 'Tap a note to discover its role'));
    }

    var rows = PROFILE_KEYS.filter(function (k) {
      return k.key !== 'family' && data.profile[k.key];
    });
    if (rows.length) {
      var dl = el('dl', 'als-profile');
      rows.forEach(function (k) {
        var item = el('div', 'als-profile__item' + (k.wide ? ' als-profile__item--wide' : ''));
        item.appendChild(el('dt', null, k.label));
        var dd = el('dd');
        if (k.chips) {
          var chips = el('div', 'als-chips');
          data.profile[k.key].split(/\s*,\s*/).forEach(function (c) {
            if (c) chips.appendChild(el('span', null, c));
          });
          dd.appendChild(chips);
        } else {
          dd.textContent = data.profile[k.key];
        }
        item.appendChild(dd);
        dl.appendChild(item);
      });
      mount.appendChild(dl);
    }
  }

  function buildFallback(mount, family, productTitle, notesInStory) {
    mount.innerHTML = '';
    mount.classList.add('als-scent--fallback');
    var head = el('div', 'als-scent__head');
    head.appendChild(el('p', 'als-scent__eyebrow', 'The fragrance'));
    head.appendChild(el('h2', 'als-scent__title', 'Discover the scent'));
    if (family) head.appendChild(el('span', 'als-scent__family', family));
    mount.appendChild(head);
    mount.appendChild(
      el(
        'p',
        'als-scent__lead',
        notesInStory
          ? 'Discover its notes in the story below — or ask our team to describe its character and help you choose.'
          : 'The full note breakdown for this fragrance isn’t listed yet. Our team can describe its character and help you choose.'
      )
    );
    var a = el('a', 'als-scent__cta');
    a.href =
      'https://wa.me/' +
      WA_NUMBER +
      '?text=' +
      encodeURIComponent('Hello Al Sultan, could you tell me about the scent of ' + productTitle + '?');
    a.target = '_blank';
    a.rel = 'noopener';
    a.innerHTML = WA_SVG + '<span>Ask our advisors</span>';
    mount.appendChild(a);
  }

  function initScent(root) {
    var mount = root.querySelector('[data-als-scent]');
    if (!mount) return;
    var desc = root.querySelector('.product-premium__description');
    var typeEl = root.querySelector('.als-pdp-type');
    var type = typeEl ? typeEl.textContent.trim() : '';
    var titleEl = root.querySelector('.product__title h1');
    var productTitle = titleEl ? titleEl.getAttribute('aria-label') || titleEl.textContent.trim() : 'this fragrance';

    var data = parseDescription(desc);
    var family = data.profile.family || (isFamilyLike(type) ? type : '');

    if (data.found) {
      buildPyramid(mount, data, family);
    } else if (!/(lotion|bath|body care|cream|soap|shower|gel)/i.test(type)) {
      // Profile items (if any) stay in the description when no notes exist.
      if (desc) {
        desc.querySelectorAll('[data-als-moved]').forEach(function (n) {
          n.removeAttribute('data-als-moved');
        });
      }
      buildFallback(mount, family, productTitle, !!desc && /\bnotes?\b/i.test(desc.textContent));
    } else {
      return;
    }

    mount.hidden = false;
    if (!REDUCED && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          if (entries[0].isIntersecting) {
            mount.classList.add('is-in');
            io.disconnect();
          }
        },
        { threshold: 0.2 }
      );
      io.observe(mount);
    } else {
      mount.classList.add('is-in');
    }
  }

  /* ── 6. Editorial description: lead paragraph and long-copy collapse ─── */

  function initStory(root) {
    var story = root.querySelector('.product-premium__story');
    var desc = root.querySelector('.product-premium__description');
    if (!story || !desc) return;

    var visible = Array.prototype.filter.call(desc.children, function (c) {
      return !c.hasAttribute('data-als-moved') && c.textContent.trim();
    });
    if (!visible.length) {
      story.hidden = true;
      return;
    }

    var first = visible[0];
    if (first.tagName === 'P' && first.textContent.trim().length > 60) first.classList.add('als-lead');

    (function () {
      var limit = Math.max(520, Math.round(window.innerHeight * 0.9));
      if (desc.scrollHeight <= limit * 1.3) return;
      desc.classList.add('als-story-body', 'is-collapsed');
      desc.style.setProperty('--als-collapse', limit + 'px');
      if (!desc.id) desc.id = 'AlsStory-' + root.dataset.sectionId;
      var btn = el('button', 'als-story-more');
      btn.type = 'button';
      btn.setAttribute('aria-expanded', 'false');
      btn.setAttribute('aria-controls', desc.id);
      btn.innerHTML = '<span>Read the full story</span>' + CARET_SVG;
      btn.addEventListener('click', function () {
        var open = desc.classList.toggle('is-collapsed') === false;
        btn.setAttribute('aria-expanded', String(open));
        btn.querySelector('span').textContent = open ? 'Show less' : 'Read the full story';
        if (open) {
          desc.querySelectorAll('.pdp-reveal').forEach(function (n) {
            n.classList.add('is-visible');
          });
        } else {
          story.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'start' });
        }
      });
      desc.parentNode.insertBefore(btn, desc.nextSibling);
    })();
  }

  /* ── 7. Add to Cart signature ────────────────────────────────────────── */

  function initAtc(root, stage, sectionId) {
    var btn = root.querySelector('#ProductSubmitButton-' + sectionId);
    var form = root.querySelector('#product-form-' + sectionId);
    if (!btn || !form) return;

    var done = el('span', 'als-atc-done');
    done.setAttribute('aria-hidden', 'true');
    done.innerHTML = CHECK_SVG + '<span>Added</span>';
    btn.appendChild(done);

    var live = el('div', 'visually-hidden');
    live.setAttribute('role', 'status');
    btn.parentNode.appendChild(live);

    function press(target, e) {
      if (REDUCED || target.disabled) return;
      var r = target.getBoundingClientRect();
      var light = el('span', 'als-press-light');
      light.style.left = e.clientX - r.left + 'px';
      light.style.top = e.clientY - r.top + 'px';
      target.appendChild(light);
      setTimeout(function () {
        light.remove();
      }, 720);
    }

    btn.addEventListener('pointerdown', function (e) {
      press(btn, e);
    });
    var sticky = root.querySelector('[data-premium-sticky-atc]');
    if (sticky) {
      sticky.addEventListener('pointerdown', function (e) {
        press(sticky, e);
      });
    }

    if (!REDUCED && 'IntersectionObserver' in window) {
      var seen = new IntersectionObserver(
        function (entries) {
          if (!entries[0].isIntersecting) return;
          seen.disconnect();
          setTimeout(function () {
            if (btn.disabled) return;
            btn.classList.add('als-sheen');
            setTimeout(function () {
              btn.classList.remove('als-sheen');
            }, 1500);
          }, 900);
        },
        { threshold: 0.9 }
      );
      seen.observe(btn);
    }

    var pendingFrom = null;
    var lastTapped = btn;
    if (sticky) {
      // pointerdown precedes the click that product-premium-motion.js forwards to the form
      sticky.addEventListener('pointerdown', function () {
        lastTapped = sticky;
      });
    }
    btn.addEventListener('pointerdown', function () {
      lastTapped = btn;
    });
    form.addEventListener(
      'submit',
      function () {
        var active = stage && stage.querySelector('.product__media-item.is-active img');
        if (!active) {
          pendingFrom = null;
          return;
        }
        var rect = active.getBoundingClientRect();
        var cy = rect.top + rect.height / 2;
        // If the bottle itself is off screen, launch from the button that was tapped.
        if (cy < 0 || cy > window.innerHeight) {
          var b = (lastTapped || btn).getBoundingClientRect();
          var s = Math.min(110, b.height * 2);
          rect = { left: b.left + b.width / 2 - s / 2, top: b.top + b.height / 2 - s / 2, width: s, height: s, bottom: b.bottom };
        }
        pendingFrom = { src: active.currentSrc || active.src, rect: rect };
        lastTapped = btn;
      },
      true
    );

    var timer = 0;
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (!event || event.source !== 'product-form') return;
        var idInput = form.querySelector('[name="id"]');
        if (idInput && String(event.productVariantId) !== String(idInput.value)) return;
        clearTimeout(timer);
        btn.classList.add('is-added');
        live.textContent = 'Added to your cart';
        timer = setTimeout(function () {
          btn.classList.remove('is-added');
          live.textContent = '';
        }, 1900);
        if (!REDUCED && pendingFrom) flyToCart(root, pendingFrom);
        pendingFrom = null;
      });
    }
  }

  function flyToCart(root, from) {
    var drawer = document.querySelector('cart-drawer');
    var handle = (root.dataset.url || '').split('/products/')[1];
    var start = performance.now();

    function findTarget() {
      if (drawer && drawer.classList.contains('active')) {
        var items = drawer.querySelectorAll('.cart-item');
        for (var i = 0; i < items.length; i++) {
          var link = items[i].querySelector('a[href*="/products/"]');
          if (handle && link && link.getAttribute('href').indexOf('/products/' + handle) !== -1) {
            return items[i].querySelector('.cart-item__image, img') || items[i];
          }
        }
        return null;
      }
      return null;
    }

    function wait() {
      var target = findTarget();
      if (target && performance.now() - start > 380) return launch(target);
      if (performance.now() - start > 1600) {
        var icon = document.querySelector('#cart-icon-bubble');
        if (icon && !(drawer && drawer.classList.contains('active'))) launch(icon);
        return;
      }
      requestAnimationFrame(wait);
    }

    function launch(target) {
      var to = target.getBoundingClientRect();
      if (!to.width) return;
      var a = from.rect;
      var vh = window.innerHeight;
      if (a.bottom < 0 || a.top > vh || !a.width) {
        var size = 120;
        a = { left: window.innerWidth / 2 - size / 2, top: vh - 220, width: size, height: size };
      } else if (a.width > 150) {
        // Launch a smaller bottle from the gallery's centre so it doesn't swamp the drawer.
        var cx = a.left + a.width / 2;
        var cy = a.top + a.height / 2;
        var h = a.height * (150 / a.width);
        a = { left: cx - 75, top: cy - h / 2, width: 150, height: h };
      }
      var ghost = document.createElement('img');
      ghost.className = 'als-ghost';
      ghost.src = from.src;
      ghost.alt = '';
      ghost.style.left = a.left + 'px';
      ghost.style.top = a.top + 'px';
      ghost.style.width = a.width + 'px';
      ghost.style.height = a.height + 'px';
      document.body.appendChild(ghost);
      var dx = to.left + to.width / 2 - (a.left + a.width / 2);
      var dy = to.top + to.height / 2 - (a.top + a.height / 2);
      var s = Math.min(to.width / a.width, to.height / a.height);
      var anim = ghost.animate(
        [
          { transform: 'translate3d(0,0,0) scale(1) rotate(0deg)', opacity: 1 },
          { transform: 'translate3d(' + dx * 0.45 + 'px,' + (dy * 0.45 - 90) + 'px,0) scale(' + (0.55 + s * 0.45) + ') rotate(-6deg)', opacity: 1, offset: 0.5 },
          { transform: 'translate3d(' + dx + 'px,' + dy + 'px,0) scale(' + s + ') rotate(0deg)', opacity: 0.2 },
        ],
        { duration: 760, easing: 'cubic-bezier(0.45, 0, 0.2, 1)' }
      );
      var landed = false;
      var land = function () {
        if (landed) return;
        landed = true;
        ghost.remove();
        target.classList.remove('als-cart-landed');
        void target.offsetWidth;
        target.classList.add('als-cart-landed');
      };
      anim.onfinish = anim.oncancel = land;
      // Never leave a ghost behind if the tab stops rendering mid-flight.
      setTimeout(land, 1200);
    }

    requestAnimationFrame(wait);
  }

  /* ── 8. Discover your next scent ─────────────────────────────────────── */

  function initDiscover(section) {
    var recs = section.querySelector('product-recommendations');
    if (!recs) return;

    function setup() {
      var track = recs.querySelector('ul.product-grid');
      if (!track || track.classList.contains('als-discover__track')) return;
      var cards = Array.prototype.slice.call(track.children);
      if (!cards.length) return;

      section.classList.add('als-discover');
      if (!REDUCED) section.classList.add('is-motion-enhanced');
      track.classList.add('als-discover__track');
      track.setAttribute('aria-label', 'Recommended products');
      cards.forEach(function (c) {
        c.classList.add('is-visible');
      });

      // The card's stretched title link doesn't reach over the image in this
      // card style, so a tap on the bottle follows the card's own link.
      track.addEventListener('click', function (e) {
        if (e.defaultPrevented || e.target.closest('a, button, input, select, label, quick-add-modal')) return;
        var card = e.target.closest('.product-premium-related__card-item');
        var link = card && card.querySelector('.card__heading a[href]');
        if (link) window.location.href = link.href;
      });

      var nav = el('div', 'als-discover__nav');
      var prev = el('button', 'als-rail__arrow als-rail__arrow--prev');
      prev.type = 'button';
      prev.setAttribute('aria-label', 'Previous products');
      prev.innerHTML = CARET_SVG;
      var next = el('button', 'als-rail__arrow als-rail__arrow--next');
      next.type = 'button';
      next.setAttribute('aria-label', 'Next products');
      next.innerHTML = CARET_SVG;
      nav.appendChild(prev);
      nav.appendChild(next);
      track.parentNode.insertBefore(nav, track.nextSibling);

      function step(dir) {
        track.scrollBy({ left: dir * track.clientWidth * 0.9, behavior: REDUCED ? 'auto' : 'smooth' });
      }
      prev.addEventListener('click', function () {
        step(-1);
      });
      next.addEventListener('click', function () {
        step(1);
      });

      var queued = false;
      function depth() {
        queued = false;
        var max = track.scrollWidth - track.clientWidth;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max - 2;
        if (REDUCED || DESKTOP_MQ.matches) {
          cards.forEach(function (c) {
            c.style.removeProperty('--als-ry');
            c.style.removeProperty('--als-tz');
          });
          return;
        }
        var tr = track.getBoundingClientRect();
        var focus = tr.left + tr.width * 0.4;
        cards.forEach(function (c) {
          var r = c.getBoundingClientRect();
          var d = clamp((r.left + r.width / 2 - focus) / tr.width, -1, 1);
          c.style.setProperty('--als-ry', (d * -16).toFixed(2) + 'deg');
          c.style.setProperty('--als-tz', (-Math.abs(d) * 70).toFixed(1) + 'px');
        });
      }
      function request() {
        if (queued) return;
        queued = true;
        requestAnimationFrame(depth);
      }
      track.addEventListener('scroll', request, { passive: true });
      window.addEventListener('resize', request);
      depth();

      if (!REDUCED && 'IntersectionObserver' in window) {
        // Trigger on the track's top edge entering the lower viewport, so
        // the entrance never depends on how tall the rail is.
        var io = new IntersectionObserver(
          function (entries) {
            if (!entries[0].isIntersecting) return;
            section.classList.add('is-discover-in');
            io.disconnect();
          },
          { rootMargin: '0px 0px -12% 0px' }
        );
        io.observe(track);
      } else {
        section.classList.add('is-discover-in');
      }
    }

    setup();
    new MutationObserver(setup).observe(recs, { childList: true, subtree: true });

    // Dawn fetches recommendations once; if that request fails the section
    // stays empty. Retry a single time after it has been in view.
    if ('IntersectionObserver' in window) {
      var retry = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) return;
        retry.disconnect();
        setTimeout(function () {
          if (!recs.innerHTML.trim() && typeof recs.loadRecommendations === 'function' && recs.dataset.productId) {
            recs.loadRecommendations(recs.dataset.productId);
          }
        }, 2500);
      });
      retry.observe(recs);
    }
  }

  /* ── 9. Chat widgets yield to purchase controls ──────────────────────── */

  // Carthike / WhatsApp float at z-index 1e6. Whenever one sits over a buy
  // control, fade it and let taps through so Add to Cart always works.
  var WIDGET_SEL =
    '#carthike-chat-button-container, #carthike-share-button-container, .wa-popup-container, .whatsapp-widget-container';

  function initWidgetYield(root, sectionId) {
    var targets = [
      root.querySelector('#ProductSubmitButton-' + sectionId),
      root.querySelector('.product-premium__actions .shopify-payment-button'),
      root.querySelector('.product-premium__actions .product-form__quantity'),
      root.querySelector('[data-premium-sticky-atc]'),
    ].filter(Boolean);
    if (!targets.length) return;

    function overlaps(a, b) {
      return a.width && b.width && !(a.bottom <= b.top || a.top >= b.bottom || a.right <= b.left || a.left >= b.right);
    }

    onScrollFrame(function () {
      var widgets = document.querySelectorAll(WIDGET_SEL);
      var hit = false;
      for (var i = 0; i < widgets.length && !hit; i++) {
        var w = widgets[i].getBoundingClientRect();
        for (var j = 0; j < targets.length; j++) {
          if (overlaps(w, targets[j].getBoundingClientRect())) {
            hit = true;
            break;
          }
        }
      }
      document.body.classList.toggle('als-widgets-yield', hit);
    });
  }

  /* ── Boot ─────────────────────────────────────────────────────────────── */

  function initProduct(root) {
    if (root.dataset.alsPdp === 'true' || root.closest('quick-add-modal')) return;
    root.dataset.alsPdp = 'true';

    var sectionId = root.dataset.sectionId || root.dataset.section;
    var stage = root.querySelector('.product-premium__media-stage');
    var purchase = root.querySelector('.product-premium__purchase');

    if (stage) {
      initStage(root, stage);
      var gallery = initGallery(root, stage);
      initViewer(root, stage, gallery);
    }
    if (purchase) splitTitle(root, purchase);
    initStatus(root, sectionId);
    initScent(root);
    initStory(root);
    initAtc(root, stage, sectionId);
    initWidgetYield(root, sectionId);
  }

  function boot() {
    document.querySelectorAll('product-info.product-premium[id^="MainProduct"]').forEach(initProduct);
    document.querySelectorAll('.product-premium-related').forEach(initDiscover);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
